import { AdminRatingsPage } from '@/pages/admin/RatingsPage';
import { CalendarPage } from '@/pages/admin/CalendarPage';
import { WorksCommunityPage } from '@/pages/admin/WorksCommunityPage';
import { WorksCommunityEntryPage } from '@/pages/student/WorksCommunityEntryPage';
import { lazy, Suspense, useEffect } from 'react';
import { ComemoracaoDaSequencia } from '@/components/gamification/ComemoracaoDaSequencia';
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import { Toaster } from 'sonner';
import { Loader2 } from 'lucide-react';
import { usePWAStore } from './stores/pwa';
import { useAuthStore } from './stores/auth';
import { scheduleTokenRefresh } from './services/api';

// Layouts (kept eager - they wrap all routes)
import { AuthLayout } from '@/components/layouts/AuthLayout';
import { StudentLayout } from '@/components/layouts/StudentLayout';
import { MemberLayout } from '@/components/layouts/HomeLayout';
import { AdminLayout } from '@/components/layouts/AdminLayout';
import { LessonLayout } from '@/components/layouts/LessonLayout';

// Guards (kept eager - they gate route access)
import { PrivateRoute } from '@/components/guards/PrivateRoute';
import { AdminRoute } from '@/components/guards/AdminRoute';
import { SurveyModal as SurveyModalGlobal } from '@/components/surveys/SurveyModal';
import { ROTAS_EM_PORTUGUES } from '@/lib/rotas';
import { PaginaNaoEncontrada } from '@/pages/student/PaginaNaoEncontrada';
import { WalkthroughTour } from '@/components/walkthrough/WalkthroughTour';

// Auth Pages (lazy)
const AgendaPage = lazy(() =>
  import('@/pages/student/AgendaPage').then((m) => ({
    default: m.AgendaPage,
  })),
);
const LoginPage = lazy(() =>
  import('@/pages/auth/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/pages/auth/ForgotPasswordPage').then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import('@/pages/auth/ResetPasswordPage').then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const DeepLinkLoginPage = lazy(() =>
  import('@/pages/auth/DeepLinkLoginPage').then((m) => ({
    default: m.DeepLinkLoginPage,
  })),
);
const DevflixAuthorizePage = lazy(() =>
  import('@/pages/auth/DevflixAuthorizePage').then((m) => ({
    default: m.DevflixAuthorizePage,
  })),
);

// Student Pages (lazy)
const HomePage = lazy(() =>
  import('@/pages/student/HomePage').then((m) => ({
    default: m.HomePage,
  })),
);
const CoursesPage = lazy(() =>
  import('@/pages/student/CoursesPageEnhanced').then((m) => ({
    default: m.CoursesPageEnhanced,
  })),
);
const CoursePage = lazy(() =>
  import('@/pages/student/CoursePage').then((m) => ({
    default: m.CoursePage,
  })),
);
const MemberExperiencePage = lazy(() =>
  import('@/pages/student/MemberExperiencePage').then((m) => ({ default: m.MemberExperiencePage })),
);
const CourseTrailPage = lazy(() =>
  import('@/pages/student/CourseTrailPage').then((m) => ({
    default: m.CourseTrailPage,
  })),
);
const LessonPage = lazy(() =>
  import('@/pages/student/LessonPage').then((m) => ({
    default: m.LessonPage,
  })),
);
const ProfilePage = lazy(() =>
  import('@/pages/student/ProfilePage').then((m) => ({
    default: m.ProfilePage,
  })),
);
const CertificatesPage = lazy(() =>
  import('@/pages/student/CertificatesPage').then((m) => ({
    default: m.CertificatesPage,
  })),
);
const RankingPage = lazy(() =>
  import('@/pages/student/RankingPage').then((m) => ({
    default: m.RankingPage,
  })),
);
const CommunityPage = lazy(() =>
  import('@/pages/student/CommunityPage').then((m) => ({
    default: m.CommunityPage,
  })),
);

// Admin Pages (lazy)
const AdminDashboardPage = lazy(() =>
  import('@/pages/admin/DashboardPage').then((m) => ({
    default: m.AdminDashboardPage,
  })),
);
const AdminCoursesPage = lazy(() =>
  import('@/pages/admin/CoursePage').then((m) => ({
    default: m.AdminCoursesPage,
  })),
);
const AdminCourseEditPage = lazy(() =>
  import('@/pages/admin/CoursePage/CourseEditPage').then((m) => ({
    default: m.CourseEditPage,
  })),
);
const AdminCourseAppearancePage = lazy(() =>
  import('@/pages/admin/CoursePage/AppearancePage').then((m) => ({
    default: m.CourseAppearancePage,
  })),
);
const AdminAccessGroupsPage = lazy(() =>
  import('@/pages/admin/AccessGroupsPage').then((m) => ({
    default: m.AdminAccessGroupsPage,
  })),
);
const AdminProductsPage = lazy(() =>
  import('@/pages/admin/ProductsPage').then((m) => ({
    default: m.AdminProductsPage,
  })),
);
const AdminMemberExperiencePage = lazy(() =>
  import('@/pages/admin/MemberExperiencePage').then((m) => ({ default: m.AdminMemberExperiencePage })),
);
const AdminAccessGroupDetailPage = lazy(() =>
  import('@/pages/admin/AccessGroupDetailPage').then((m) => ({
    default: m.AdminAccessGroupDetailPage,
  })),
);
const AdminCertificatesPage = lazy(() =>
  import('@/pages/admin/CertificatesPage').then((m) => ({
    default: m.AdminCertificatesPage,
  })),
);
const AdminCertificateEditorPage = lazy(() =>
  import('@/pages/admin/CertificateEditorPage').then((m) => ({
    default: m.AdminCertificateEditorPage,
  })),
);
const CertificateValidationPage = lazy(() =>
  import('@/pages/public/CertificateValidationPage').then((m) => ({
    default: m.CertificateValidationPage,
  })),
);
const AdminUsersPage = lazy(() =>
  import('@/pages/admin/UsersPage').then((m) => ({
    default: m.AdminUsersPage,
  })),
);
const AdminUserEditPage = lazy(() =>
  import('@/pages/admin/UserEditPage').then((m) => ({
    default: m.AdminUserEditPage,
  })),
);
const AdminCmsPage = lazy(() =>
  import('@/pages/admin/CmsPage').then((m) => ({ default: m.AdminCmsPage })),
);
const AdminMenusPage = lazy(() =>
  import('@/pages/admin/MenusPage').then((m) => ({ default: m.AdminMenusPage })),
);
const AdminFormationBannersPage = lazy(() =>
  import('@/pages/admin/FormationBannersPage').then((m) => ({ default: m.AdminFormationBannersPage })),
);
const AdminGamificationPage = lazy(() =>
  import('@/pages/admin/GamificationPage').then((m) => ({
    default: m.AdminGamificationPage,
  })),
);
const AdminSettingsPage = lazy(() =>
  import('@/pages/admin/SettingsPage').then((m) => ({
    default: m.AdminSettingsPage,
  })),
);
const AdminEmailTemplatesPage = lazy(() =>
  import('@/pages/admin/EmailTemplatesPage').then((m) => ({
    default: m.AdminEmailTemplatesPage,
  })),
);
const AdminNotificationsPage = lazy(() =>
  import('@/pages/admin/NotificationsPage').then((m) => ({ default: m.AdminNotificationsPage })),
);
const AdminPopupsPage = lazy(() =>
  import('@/pages/admin/PopupsPage').then((m) => ({ default: m.AdminPopupsPage })),
);
const AdminCommunicationsPage = lazy(() =>
  import('@/pages/admin/CommunicationsPage').then((m) => ({ default: m.AdminCommunicationsPage })),
);
const CommunicationPreferences = lazy(() =>
  import('@/components/communications/CommunicationPreferences').then((m) => ({ default: m.CommunicationPreferences })),
);
const AdminWebhooksPage = lazy(() =>
  import('@/pages/admin/WebhooksPage').then((m) => ({
    default: m.WebhooksPage,
  })),
);
const AdminCustomFieldsPage = lazy(() =>
  import('@/pages/admin/CustomFieldsPage').then((m) => ({
    default: m.AdminCustomFieldsPage,
  })),
);
const AdminWalkthroughPage = lazy(() =>
  import('@/pages/admin/WalkthroughPage').then((m) => ({
    default: m.AdminWalkthroughPage,
  })),
);
const AdminEngagementPage = lazy(() =>
  import('@/pages/admin/EngagementPage').then((m) => ({
    default: m.AdminEngagementPage,
  })),
);
const AdminCommentInboxPage = lazy(() =>
  import('@/pages/admin/CommentInboxPage').then((m) => ({
    default: m.AdminCommentInboxPage,
  })),
);
const AdminWalkthroughEditPage = lazy(() =>
  import('@/pages/admin/WalkthroughEditPage').then((m) => ({
    default: m.AdminWalkthroughEditPage,
  })),
);
const AdminSurveysPage = lazy(() =>
  import('@/pages/admin/SurveysPage').then((m) => ({
    default: m.AdminSurveysPage,
  })),
);
const AdminSurveyEditPage = lazy(() =>
  import('@/pages/admin/SurveyEditPage').then((m) => ({
    default: m.AdminSurveyEditPage,
  })),
);
const AdminCategoriesPage = lazy(() =>
  import('@/pages/admin/CategoriesPage').then((m) => ({
    default: m.AdminCategoriesPage,
  })),
);
const AdminPWAPage = lazy(() =>
  import('@/pages/admin/PWAPage').then((m) => ({
    default: m.PWAPage,
  })),
);
const AdminModulesPage = lazy(() =>
  import('@/pages/admin/ModulesPage').then((m) => ({
    default: m.ModulesPage,
  })),
);
const AdminModuleEditPage = lazy(() =>
  import('@/pages/admin/ModulesPage/ModuleEditPage').then((m) => ({
    default: m.ModuleEditPage,
  })),
);
const TermsAcceptancePage = lazy(() =>
  import('@/pages/auth/TermsAcceptancePage').then((m) => ({
    default: m.TermsAcceptancePage,
  })),
);

const CourseCoversPocPage = lazy(() =>
  import('@/pages/demo/CourseCoversPocPage').then((m) => ({
    default: m.CourseCoversPocPage,
  })),
);
const LessonExperiencePocPage = lazy(() =>
  import('@/pages/demo/LessonExperiencePocPage').then((m) => ({
    default: m.LessonExperiencePocPage,
  })),
);

/** Redirects to /terms-acceptance if user hasn't accepted terms */
function TermsGuard({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  if (user && !(user as typeof user & { termsAcceptedAt?: string | null }).termsAcceptedAt) {
    return <Navigate to="/terms-acceptance" replace />;
  }
  return <>{children}</>;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function PageLoader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Loader2 className="text-primary h-8 w-8 animate-spin" />
    </div>
  );
}

function App() {
  const { initialize } = usePWAStore();
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    // Initialize PWA on app start
    initialize();
  }, [initialize]);

  useEffect(() => {
    // Schedule proactive token refresh for already-logged-in users
    if (isAuthenticated) {
      scheduleTokenRefresh(432000); // 5 days default
    }
  }, [isAuthenticated]);

  return (
    <>
      <ScrollToTop />
      {/* o fogo da sequência: abre ao concluir a primeira aula do dia (a aula fica fora do StudentLayout) */}
      <ComemoracaoDaSequencia />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {import.meta.env.DEV && (
            <Route path="/poc/capas" element={<CourseCoversPocPage />} />
          )}
          {import.meta.env.DEV && (
            <Route path="/poc/aulas" element={<LessonExperiencePocPage />} />
          )}
          {/* Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Route>

          {/* Deep Link — standalone (no layout) */}
          <Route path="/auth/deep-link" element={<DeepLinkLoginPage />} />
          <Route
            path="/sso/devflix/authorize"
            element={
              <PrivateRoute>
                <DevflixAuthorizePage />
              </PrivateRoute>
            }
          />

          {/* Validação pública de certificado — sem auth */}
          <Route
            path="/validar-certificado"
            element={<CertificateValidationPage />}
          />
          <Route
            path="/validar-certificado/:code"
            element={<CertificateValidationPage />}
          />

          {/* Terms Acceptance (must be BEFORE TermsGuard routes) */}
          <Route
            path="/terms-acceptance"
            element={
              <PrivateRoute>
                <TermsAcceptancePage />
              </PrivateRoute>
            }
          />

          {/* Member navigation stays mounted across pages and lessons. */}
          <Route
            element={
              <PrivateRoute>
                <TermsGuard>
                  <MemberLayout />
                </TermsGuard>
              </PrivateRoute>
            }
          >
            {/* Lesson Routes - Immersive Layout (no sidebar) */}
            <Route element={<LessonLayout />}>
              <Route path="/lesson/:lessonId" element={<LessonPage />} />
              <Route
                path="/courses/:slug/lessons/:lessonId"
                element={<LessonPage />}
              />
            </Route>

            {/* Student Routes */}
            <Route element={<StudentLayout />}>
              <Route path="/home" element={<HomePage />} />
              <Route path="/courses" element={<CoursesPage />} />
              <Route path="/areas/:areaId" element={<MemberExperiencePage />} />
              <Route path="/courses/:slug" element={<CoursePage />} />
              <Route path="/courses/:slug/trail" element={<CourseTrailPage />} />
              <Route path="/ranking" element={<RankingPage />} />
              <Route path="/certificates" element={<CertificatesPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/communication-preferences" element={<CommunicationPreferences />} />
              <Route path="/community" element={<CommunityPage />} />
              <Route path="/works-ai" element={<WorksCommunityEntryPage />} />
              {/* Agenda ao vivo: as aulas LIVE dos cursos do aluno (o menu usa /eventos) */}
              <Route path="/events" element={<AgendaPage />} />
              {/* Item "Trilhas de carreira" do menu (admin): mesma página da trilha, sem slug */}
              <Route path="/trilhas" element={<CourseTrailPage />} />
              {/* Endereços em português que o menu usa (lib/rotas.ts) */}
              {Object.entries(ROTAS_EM_PORTUGUES).map(([de, para]) => (
                <Route
                  key={de}
                  path={de}
                  element={<Navigate to={para} replace />}
                />
              ))}
              <Route path="/cursos/:slug" element={<CursoEmPortugues />} />
              {/* Endereço que não existe: diz o que houve e dá saída (antes caía no Início calado) */}
              <Route path="*" element={<PaginaNaoEncontrada />} />
            </Route>
          </Route>

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <TermsGuard>
                  <AdminLayout />
                </TermsGuard>
              </AdminRoute>
            }
          >
            <Route index element={<AdminDashboardPage />} />
            <Route path="courses" element={<AdminCoursesPage />} />
            <Route path="courses/:id" element={<AdminCourseEditPage />} />
            <Route path="modules" element={<AdminModulesPage />} />
            <Route path="modules/:id" element={<AdminModuleEditPage />} />
            <Route path="categories" element={<AdminCategoriesPage />} />
            <Route path="access-groups" element={<AdminAccessGroupsPage />} />
            <Route path="products" element={<AdminProductsPage />} />
            <Route path="member-experience" element={<AdminMemberExperiencePage />} />
            <Route
              path="access-groups/:id"
              element={<AdminAccessGroupDetailPage />}
            />
            <Route path="certificates" element={<AdminCertificatesPage />} />
            <Route path="ratings" element={<AdminRatingsPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="works-community" element={<WorksCommunityPage />} />
            <Route
              path="certificates/:id/editor"
              element={<AdminCertificateEditorPage />}
            />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="users/:id/edit" element={<AdminUserEditPage />} />
            <Route path="cms" element={<AdminCmsPage />} />
            <Route path="menus" element={<AdminMenusPage />} />
            <Route path="formation-banners" element={<AdminFormationBannersPage />} />
            <Route path="gamification" element={<AdminGamificationPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="notifications" element={<AdminNotificationsPage />} />
            <Route path="popups" element={<AdminPopupsPage />} />
            <Route path="communications" element={<AdminCommunicationsPage />} />
            <Route
              path="email-templates"
              element={<AdminEmailTemplatesPage />}
            />
            <Route path="webhooks" element={<AdminWebhooksPage />} />
            <Route path="custom-fields" element={<AdminCustomFieldsPage />} />
            <Route path="surveys" element={<AdminSurveysPage />} />
            <Route path="surveys/:id" element={<AdminSurveyEditPage />} />
            <Route path="engajamento" element={<AdminEngagementPage />} />
            <Route path="duvidas" element={<AdminCommentInboxPage />} />
            <Route
              path="duvidas/:commentId"
              element={<AdminCommentInboxPage />}
            />
            <Route path="walkthrough" element={<AdminWalkthroughPage />} />
            <Route
              path="walkthrough/:id"
              element={<AdminWalkthroughEditPage />}
            />
            <Route path="pwa" element={<AdminPWAPage />} />
          </Route>

          {/* Course appearance editor — standalone (no admin layout), opens in new tab */}
          <Route
            path="/admin/courses/:id/appearance"
            element={
              <AdminRoute>
                <TermsGuard>
                  <AdminCourseAppearancePage />
                </TermsGuard>
              </AdminRoute>
            }
          />

          {/* Redirects */}
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="/dashboard" element={<Navigate to="/home" replace />} />

          {/* o curinga mora dentro da área do aluno (PaginaNaoEncontrada) */}
        </Routes>
      </Suspense>

      <Toaster
        toastOptions={{
          style: {
            width: 'fit-content',
            minWidth: 'unset',
            maxWidth: 'calc(100vw - 32px)',
          },
        }}
      />
      <SurveyModalGlobal />
      <WalkthroughTour />
    </>
  );
}

/** `/cursos/<curso>` do menu leva para a página do curso em `/courses/<curso>`. */
function CursoEmPortugues() {
  const { slug } = useParams();
  return <Navigate to={`/courses/${slug}`} replace />;
}

export default App;
